import React, { useRef, useState } from 'react';
import { CircleDot, Moon, Sun } from 'lucide-react';
import { THEMES, applyTheme, saveTheme, getCurrentTheme } from '../theme/theme';

const ICONS = { stark: CircleDot, dark: Moon, light: Sun };

/**
 * Three-way theme switch: Stark / Classic Dark / Classic Light.
 *
 * A radio group (one tab stop, arrow keys move and select, as the ARIA
 * pattern asks). The choice is written to <html data-theme> and saved to
 * localStorage; public/index.html re-applies it before first paint.
 */
export default function ThemeToggle() {
  const [theme, setTheme] = useState(getCurrentTheme);
  const refs = useRef({});

  const choose = (id, focus) => {
    const applied = applyTheme(id);
    saveTheme(applied);
    setTheme(applied);
    if (focus && refs.current[applied]) refs.current[applied].focus();
  };

  const onKeyDown = (e) => {
    const i = THEMES.findIndex((t) => t.id === theme);
    let next = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = THEMES[(i + 1) % THEMES.length];
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = THEMES[(i - 1 + THEMES.length) % THEMES.length];
    if (e.key === 'Home') next = THEMES[0];
    if (e.key === 'End') next = THEMES[THEMES.length - 1];
    if (next) { e.preventDefault(); choose(next.id, true); }
  };

  return (
    <div className="theme-switch" role="radiogroup" aria-label="Theme" onKeyDown={onKeyDown}>
      {THEMES.map((t) => {
        const Icon = ICONS[t.id];
        const checked = theme === t.id;
        return (
          <button
            key={t.id}
            ref={(el) => { refs.current[t.id] = el; }}
            type="button"
            role="radio"
            aria-checked={checked}
            aria-label={t.label}
            title={`${t.label} theme`}
            tabIndex={checked ? 0 : -1}
            onClick={() => choose(t.id)}
          >
            <Icon size={14} strokeWidth={2.2} aria-hidden="true" />
          </button>
        );
      })}
    </div>
  );
}
